import { cartService } from '@/services/cart.service';
import { emailService } from '@/services/email.service';
import { FreightOrVatParams, Product, UpdateCartItemRequest } from '@/types/cart';
import { useQuery, useMutation } from '@tanstack/react-query';
import { larger } from 'mathjs';

export const useQueryCartList = () => {
  return useQuery({
    queryKey: ['CART', 'LIST'],
    queryFn: async () => {
      const res = await cartService.getCartList();
      const products = res.data?.products || [];
      products.forEach((p) => {
        if (p.productStatus === 'DISCONTINUED') {
          p.quantity = 1;
        }
      });
      res.data.products = products;
      return res.data;
    },
  });
};

export const useUpdateProduct = () => {
  return useMutation({
    mutationFn: (params: UpdateCartItemRequest) => cartService.updateCart(params),
  });
};

export const useUpdateProducts = () => {
  return useMutation({
    mutationFn: (
      params: Array<
        UpdateCartItemRequest & {
          catalogNumber: string;
        }
      >
    ) => cartService.updateProducts(params),
  });
};

export const useAddProductToCart = () => {
  return useMutation({
    mutationFn: (params: Pick<Product, 'catalogNumber' | 'quantity'>) => cartService.addProductToCart(params),
  });
};

export const useGetProductByCatalogId = () => {
  return useMutation({
    mutationFn: (catalogNumber: string) => cartService.getProductByCatalogId(catalogNumber),
  });
};

export const useDeleteProduct = () => {
  return useMutation({
    mutationFn: (ids: string[]) => cartService.deleteCart(ids),
  });
};

export const useQueryFreightAndVat = (data: FreightOrVatParams) => {
  return useQuery({
    queryKey: ['FREIGHT', 'VAT', data],
    queryFn: async () => {
      const response = await cartService.getFreightOrVat(data);
      return response;
    },
    staleTime: 1000 * 60 * 10,
    enabled:
      data && larger(data.totalPrice, '0') && Array.isArray(data.itemList) && data.itemList.length > 0 ? true : false,
  });
};

export const useSendEmail = () => {
  return useMutation((emailContent: { cartId?: number; products: Product[]; emails: string[] }) => {
    return emailService.sendEmail(emailContent);
  });
};

export const useGetQuotationData = () => {
  return useMutation((addressKey: string) => {
    return cartService.getQuotationData(addressKey);
  });
};
export const useGetFWOData = () => {
  return useMutation((param: { billToId: string; shipTo: string; addressKey: string; promotionType: string }) => {
    return cartService.getFWOData(param);
  });
};
export const useQueryPromotionData = () => {
  return useMutation((param: { pageType: string; itemList: any }) => {
    return cartService.getPromotionData(param);
  });
};
